import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {isEmpty} from '@shared/helpers/lang'
import {deepMergeWithValid} from '@shared/helpers/object'

import {updatePaymentBenefitInfo, updatePayMoneyReward, resetPurchaseReward} from '@web/actions/benefitInfo'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {getResultInfo} from '@web/actions/result'
import {
    getConfirmedPurchaseRewardPoint,
    getConfirmedReviewRewardTotalPoint,
    getConfirmedBenefitCardRewardPoint,
    fetchExpectedRewardPoint,
    getExpectedRewardPointV2,
    getExpectedMembershipPoint,
    getReviewRewardInfo,
    getBookingReward,
    waitRewardApi,
} from '@web/actions/rewardPoint'
import {APIStatus, OrderSheetPageSet} from '@web/core/constants'
import {
    CashTradeReasonClass,
    RewardState,
    RewardType,
    BundleIdType,
    ReviewSubType,
    PurchaseSubType,
    RewardTypeKeys,
    createInitialPurchaseRewardPoint,
    createInitialReviewRewardPoint,
    getInitialState,
    ReviewItemSubTypeMap,
    refinePurchaseRewardPoint,
    refineReviewRewardPoint,
    convertExpectRewardInfo,
} from '@web/slices/reward/slice'

import {MakeSliceOptions, RewardReducers} from './interfaces'

export default (options?: MakeSliceOptions) =>
    createSlice<RewardState, RewardReducers, 'reward', SliceSelectors<RewardState>>({
        name: 'reward',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(waitRewardApi, (state) => {
                    state.apiStatus['PURCHASE'] = APIStatus.PENDING
                })
                .addCase(fetchExpectedRewardPoint.fulfilled, (state, {payload}) => {
                    if (
                        payload.pageSet === OrderSheetPageSet.MALL ||
                        payload.pageSet === OrderSheetPageSet.EASYBOOKING
                    ) {
                        const {purchaseRewardPoint, reviewRewardPoint, cardEasyPayRewardPoint} = payload.data
                        state.cardEasyPayRewardPoint = Math.floor(cardEasyPayRewardPoint)

                        // member 맴버십 추가적립 의 null 을 merge 시 기본 0원 적용
                        /**
                         *  @CHECK
                         * - total 금액은 selector에서 재계산, 명칭은 초기화된 값 사용 필요
                         */
                        state.purchaseRewardPoint = {
                            ...deepMergeWithValid(purchaseRewardPoint, createInitialPurchaseRewardPoint()),
                            total: createInitialPurchaseRewardPoint().total,
                        }

                        state.reviewRewardPoint = deepMergeWithValid(
                            reviewRewardPoint,
                            createInitialReviewRewardPoint(),
                        )
                    }
                    if (payload.pageSet === OrderSheetPageSet.EASYBOOKING) {
                        const {purchaseRewardPoint, easybookingRewardPoint, easybookingRewardPointsByTitle} =
                            payload.data
                        // 예약 추가적립
                        if (easybookingRewardPointsByTitle) {
                            state.easyBookingRewardPoint.total = easybookingRewardPoint ?? 0
                            state.easyBookingRewardPoint.pointsByTitle = easybookingRewardPointsByTitle
                        }
                        state.expectedChargePoint = purchaseRewardPoint.total.expectedChargePoint
                        state.expectedNaverBankbook = purchaseRewardPoint.total.expectedNaverBankbook
                    }
                    if (payload.pageSet === OrderSheetPageSet.SELLER) {
                        const {
                            cardEasyPayRewardPoint,
                            purchaseRewardPointsByItemId,
                            expectedChargePoint,
                            expectedNaverBankbook,
                            reviewRewardPointsByItemId,
                            logeyeRewardPointListsByItemId,
                        } = payload.data

                        /** @see https://oss.fin.navercorp.com/payOrder/order-qa/issues/4429 */
                        state.cardEasyPayRewardPoint = Math.floor(cardEasyPayRewardPoint)
                        state.purchaseRewardPoint = refinePurchaseRewardPoint(
                            purchaseRewardPointsByItemId,
                            logeyeRewardPointListsByItemId,
                        )
                        state.reviewRewardPoint = refineReviewRewardPoint(reviewRewardPointsByItemId)
                        state.expectedChargePoint = expectedChargePoint
                        state.expectedNaverBankbook = expectedNaverBankbook
                    }
                })
                .addCase(getExpectedRewardPointV2.fulfilled, (state, {payload: {items}}) => {
                    if (isEmpty(items)) {
                        return state
                    }

                    const {purchaseRewardPoint, expectedNaverBankbook, expectedChargePoint} = convertExpectRewardInfo(
                        state,
                        items,
                    )
                    state.purchaseRewardPoint = purchaseRewardPoint
                    state.expectedNaverBankbook = expectedNaverBankbook
                    state.expectedChargePoint = expectedChargePoint

                    return state
                })
                .addCase(getResultInfo.fulfilled, (state, {payload: {pay}}) => {
                    state.benefitCardRewardTid = pay.benefitCardRewardTid
                })
                .addCase(getConfirmedPurchaseRewardPoint.pending, (state) => {
                    state.apiStatus = {...state.apiStatus, PURCHASE: APIStatus.FETCHING}
                })
                /**
                 * @CHECK
                 * - 모든 타입의 적립유형을 모두 item에 넣고있는데, 재조회는 적립유형별로 따로 가능
                 * - 저장할때, item내에 해당 적립유형이 아닌것들 + 새로 조회한 값 저장
                 */
                .addCase(getConfirmedPurchaseRewardPoint.fulfilled, (state, {payload: purchaseRewardItems}) => {
                    const noPurchaseItems = state.items.filter((item) => item.type !== RewardType.PURCHASE)

                    state.items = [
                        ...noPurchaseItems,
                        ...purchaseRewardItems.flatMap((item) => {
                            return item.acmAmount.map((amountItem) => {
                                if (amountItem.cashTradeReasonType === CashTradeReasonClass.PROMOTION_ACM) {
                                    return {
                                        type: RewardType.EVENT,
                                        title: item.name,
                                        amount: amountItem.amount,
                                        bundleIdType: BundleIdType.ORDER_NO,
                                        bundleId: amountItem.productOrderNo,
                                        hasMembershipBenefit: item.membershipAcmYn,
                                        ...(item.membershipAcmYn && {subType: PurchaseSubType.MEMBERSHIP}),
                                    }
                                }
                                return {
                                    type: RewardType.PURCHASE,
                                    title: item.name,
                                    amount: amountItem.amount,
                                    bundleIdType: BundleIdType.ORDER_NO,
                                    bundleId: amountItem.productOrderNo,
                                    hasMembershipBenefit: item.membershipAcmYn,
                                    ...(item.membershipAcmYn && {subType: PurchaseSubType.MEMBERSHIP}),
                                }
                            })
                        }),
                    ]

                    state.apiStatus = {...state.apiStatus, PURCHASE: APIStatus.RESOLVED}
                })
                .addCase(getConfirmedPurchaseRewardPoint.rejected, (state) => {
                    state.apiStatus = {...state.apiStatus, PURCHASE: APIStatus.REJECTED}
                })
                .addCase(getConfirmedReviewRewardTotalPoint.pending, (state) => {
                    state.apiStatus = {...state.apiStatus, REVIEW: APIStatus.FETCHING}
                })
                .addCase(getConfirmedReviewRewardTotalPoint.fulfilled, (state, {payload: reviewTotalPoint}) => {
                    const noReviewItems = state.items.filter((item) => item.type !== RewardType.REVIEW)

                    state.items = [
                        ...noReviewItems,
                        {
                            type: RewardType.REVIEW,
                            amount: reviewTotalPoint.acmAmount,
                            hasMembershipBenefit: false,
                            subType: ReviewSubType.REVIEW_TOTAL,
                        },
                    ]

                    state.apiStatus = {...state.apiStatus, REVIEW: APIStatus.RESOLVED}
                })
                .addCase(getConfirmedReviewRewardTotalPoint.rejected, (state) => {
                    state.apiStatus = {...state.apiStatus, REVIEW: APIStatus.REJECTED}
                })
                .addCase(getConfirmedBenefitCardRewardPoint.pending, (state) => {
                    state.apiStatus = {...state.apiStatus, BENEFIT_CARD: APIStatus.FETCHING}
                })
                .addCase(getConfirmedBenefitCardRewardPoint.fulfilled, (state, {payload: {result}}) => {
                    const noBenefitCardItems = state.items.filter((item) => item.type !== RewardType.BENEFIT_CARD)

                    if (result) {
                        state.items = [
                            ...noBenefitCardItems,
                            {
                                type: RewardType.BENEFIT_CARD,
                                amount: Math.floor(result?.benefitAmount ?? result.netBenefitExpectedAmount),
                                hasMembershipBenefit: false,
                            },
                        ]
                    }

                    state.apiStatus = {...state.apiStatus, BENEFIT_CARD: APIStatus.RESOLVED}
                })
                .addCase(getConfirmedBenefitCardRewardPoint.rejected, (state) => {
                    state.apiStatus = {...state.apiStatus, BENEFIT_CARD: APIStatus.REJECTED}
                })
                .addCase(getExpectedMembershipPoint.pending, (state) => {
                    state.apiStatus = {...state.apiStatus, EXPECTED_MEMBERSHIP: APIStatus.FETCHING}
                })
                .addCase(getExpectedMembershipPoint.fulfilled, (state, {payload: expectedMembershipItems}) => {
                    const noExpectedMembershipItems = state.items.filter(
                        (item) => item.type !== RewardType.EXPECTED_MEMBERSHIP,
                    )

                    state.items = [
                        ...noExpectedMembershipItems,
                        ...expectedMembershipItems.products.map((product) => {
                            return {
                                type: RewardType.EXPECTED_MEMBERSHIP,
                                title: '멤버십 추가적립',
                                amount: product.expectPoint,
                                hasMembershipBenefit: true,
                                bundleId: product.productOrderNo,
                            }
                        }),
                    ]
                    state.apiStatus = {...state.apiStatus, EXPECTED_MEMBERSHIP: APIStatus.RESOLVED}
                })
                .addCase(getExpectedMembershipPoint.rejected, (state) => {
                    state.apiStatus = {...state.apiStatus, EXPECTED_MEMBERSHIP: APIStatus.REJECTED}
                })
                .addCase(getReviewRewardInfo.pending, (state) => {
                    state.apiStatus = {...state.apiStatus, REVIEW: APIStatus.FETCHING}
                })
                .addCase(getReviewRewardInfo.fulfilled, (state, {payload: {expectedInfo, writtenReviewInfo}}) => {
                    const noReviewItems = state.items.filter((item) => item.type !== RewardType.REVIEW)

                    state.items = [
                        ...noReviewItems,
                        ...expectedInfo
                            .map((item) => {
                                const reviewItems = Object.entries(item)
                                    .filter(
                                        ([key]) =>
                                            ReviewItemSubTypeMap[key] && ReviewItemSubTypeMap[key] in ReviewSubType,
                                    )
                                    .map(([name, value]) => {
                                        return {
                                            productOrderNo: item.productOrderNo,
                                            title: name,
                                            amount: +value,
                                        }
                                    })

                                /**
                                 *  @check
                                 * - 리뷰적립의 경우 지면마다 노출하는 title명이 다르기 때문에, subType을 두고 활용
                                 */
                                return reviewItems.map((reviewItem) => {
                                    return {
                                        type: RewardType.REVIEW,
                                        amount: reviewItem.amount,
                                        BundleIdType: BundleIdType.ITEM_ID,
                                        bundleId: item.productOrderNo,
                                        hasMembershipBenefit: false,
                                        subType: ReviewItemSubTypeMap[reviewItem.title],
                                    }
                                })
                            })
                            .flat(),
                        ...writtenReviewInfo.map((item) => {
                            return {
                                type: RewardType.REVIEW,
                                amount: item.cashTradeAmount,
                                BundleIdType: BundleIdType.ITEM_ID,
                                bundleId: item.productOrderNo,
                                hasMembershipBenefit: false,
                                subType: ReviewItemSubTypeMap[item.cashTradeReasonClass],
                            }
                        }),
                    ]

                    state.apiStatus = {...state.apiStatus, REVIEW: APIStatus.RESOLVED}
                })
                .addCase(getReviewRewardInfo.rejected, (state) => {
                    state.apiStatus = {...state.apiStatus, REVIEW: APIStatus.REJECTED}
                })
                .addCase(getBookingReward.pending, (state) => {
                    state.apiStatus = {...state.apiStatus, EASY_BOOKING: APIStatus.FETCHING}
                })
                .addCase(getBookingReward.fulfilled, (state, {payload}) => {
                    const noEasyBookingItems = state.items.filter((item) => item.type !== RewardType.EASY_BOOKING)

                    state.items = [
                        ...noEasyBookingItems,
                        ...payload.map((item) => {
                            return {
                                type: RewardType.EASY_BOOKING,
                                amount: item.amount,
                                title: item.title,
                                hasMembershipBenefit: false,
                            }
                        }),
                    ]
                    state.apiStatus = {...state.apiStatus, EASY_BOOKING: APIStatus.RESOLVED}
                })
                .addCase(getBookingReward.rejected, (state) => {
                    state.apiStatus = {...state.apiStatus, EASY_BOOKING: APIStatus.REJECTED}
                })
                .addCase(fetchExpectedRewardPoint.rejected, (state) => {
                    return {
                        ...getInitialState(),
                        apiStatus: {
                            ...state.apiStatus,
                            PURCHASE: APIStatus.REJECTED,
                            REVIEW: APIStatus.REJECTED,
                            BENEFIT_CARD: APIStatus.REJECTED,
                            EXPECTED_MEMBERSHIP: APIStatus.REJECTED,
                            EASY_BOOKING: APIStatus.REJECTED,
                            EVENT: APIStatus.REJECTED,
                        },
                    }
                })
                .addCase(getExpectedRewardPointV2.rejected, (state, {payload}) => {
                    // API 실패 케이스인 경우에만 데이터 초기화
                    if (payload?.code !== 'API_ERROR') {
                        RewardTypeKeys.forEach((key) => {
                            state.apiStatus[key] = APIStatus.REJECTED
                        })
                        return state
                    }

                    return {
                        ...getInitialState(),
                        apiStatus: {
                            ...state.apiStatus,
                            PURCHASE: APIStatus.REJECTED,
                            REVIEW: APIStatus.REJECTED,
                            BENEFIT_CARD: APIStatus.REJECTED,
                            EXPECTED_MEMBERSHIP: APIStatus.REJECTED,
                            EASY_BOOKING: APIStatus.REJECTED,
                            EVENT: APIStatus.REJECTED,
                        },
                    }
                })
                .addCase(updatePaymentBenefitInfo.pending, (state) => {
                    state.apiStatus = {
                        ...state.apiStatus,
                        EVENT: APIStatus.PENDING,
                        PURCHASE: APIStatus.PENDING,
                        ...(state.apiStatus.PURCHASE === APIStatus.DISABLED && {PURCHASE: APIStatus.DISABLED}),
                    }
                })
                .addCase(updatePaymentBenefitInfo.fulfilled, (state, {payload}) => {
                    const {accumulationEstimations} = payload

                    state.apiStatus = {
                        ...state.apiStatus,
                        EVENT: APIStatus.RESOLVED,
                        PURCHASE: APIStatus.RESOLVED,
                        ...(state.apiStatus.PURCHASE === APIStatus.DISABLED && {PURCHASE: APIStatus.DISABLED}),
                    }
                    state.items = accumulationEstimations
                })
                .addCase(updatePaymentBenefitInfo.rejected, (state, {payload}) => {
                    state.apiStatus = {
                        ...state.apiStatus,
                        EVENT: APIStatus.REJECTED,
                        PURCHASE: APIStatus.REJECTED,
                        ...(state.apiStatus.PURCHASE === APIStatus.DISABLED && {PURCHASE: APIStatus.DISABLED}),
                    }

                    const isOutdatedError =
                        payload && 'exceptionCode' in payload && payload.exceptionCode === 'Outdated'
                    if (!isOutdatedError) {
                        state.items = []
                    }
                })
                .addCase(resetPurchaseReward, (state) => {
                    state.apiStatus = {...state.apiStatus, PURCHASE: APIStatus.IDLE}
                    state.items = []
                })
                .addCase(updatePayMoneyReward.fulfilled, (state, {payload}) => {
                    const {totalExpectedRewardAmount, expectedChargePoint} = payload

                    state.totalExpectedPurchaseRewardPoint = totalExpectedRewardAmount
                    state.expectedChargePoint = expectedChargePoint
                })
            options?.extraReducers?.(builder)
            builder
                .addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                    return payload.reward ?? state
                })
                .addMatcher(isAnyOf(fetchExpectedRewardPoint.pending, getExpectedRewardPointV2.pending), (state) => {
                    RewardTypeKeys.forEach((key) => {
                        state.apiStatus[key] = APIStatus.FETCHING
                    })
                })
                .addMatcher(
                    isAnyOf(fetchExpectedRewardPoint.fulfilled, getExpectedRewardPointV2.fulfilled),
                    (state) => {
                        RewardTypeKeys.forEach((key) => {
                            state.apiStatus[key] = APIStatus.RESOLVED
                        })
                    },
                )
        },
    })
